import React, { useEffect, useRef, useState } from 'react';
import { ShareNetwork, EnvelopeSimple, MicrosoftOutlookLogo, WhatsappLogo, GoogleLogo } from '@phosphor-icons/react';

import { notify } from '../lib/notify';
/**
 * Reusable share menu that opens the user's OWN Gmail / Outlook / WhatsApp with
 * a pre-composed message. Zero backend email dispatch — the tenant's user
 * sends from their own account.
 *
 * Props:
 *   getPayload — async fn returning { subject, body, to?: string }
 *     Kept lazy because most callers need to hit /generate-share-link first.
 *   label — button label (default 'Share')
 *   variant — 'button' | 'icon' — style of the trigger
 *   className — extra classes for the trigger
 *   testid — data-testid prefix (default 'share-menu')
 *   toEmail — optional prefilled recipient email
 *
 * Web compose URLs used:
 *   Gmail:    https://mail.google.com/mail/?view=cm&fs=1&to=…&su=…&body=…
 *   Outlook:  https://outlook.office.com/mail/deeplink/compose?to=…&subject=…&body=…
 *   WhatsApp: https://wa.me/?text=…
 *
 * If the user is already logged into that service in another tab, the compose
 * window pops up pre-filled. Otherwise they see the login page → sign in →
 * compose. No API keys, no cost, no per-tenant sender config.
 */
export default function ShareMenu({
  getPayload,
  label = 'Share',
  variant = 'button',
  className = '',
  testid = 'share-menu',
  toEmail = '',
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const openWith = async (buildUrl) => {
    setLoading(true);
    try {
      const payload = await getPayload();
      if (!payload) return;
      const url = buildUrl(payload);
      window.open(url, '_blank', 'noopener,noreferrer');
      setOpen(false);
    } catch (e) {
      notify(e?.response?.data?.detail || e?.message || 'Could not prepare share link');
    } finally {
      setLoading(false);
    }
  };

  const gmail = () => openWith(({ subject, body, to }) => {
    const target = to || toEmail || '';
    const params = new URLSearchParams({
      view: 'cm', fs: '1', to: target, su: subject || '', body: body || '',
    });
    return `https://mail.google.com/mail/?${params.toString()}`;
  });

  const outlook = () => openWith(({ subject, body, to }) => {
    const target = to || toEmail || '';
    const params = new URLSearchParams({
      to: target, subject: subject || '', body: body || '',
    });
    return `https://outlook.office.com/mail/deeplink/compose?${params.toString()}`;
  });

  const whatsapp = () => openWith(({ subject, body }) => {
    const text = [subject, body].filter(Boolean).join('\n\n');
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  });

  const trigger = variant === 'icon' ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      className={`w-8 h-8 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition ${className}`}
      data-testid={`${testid}-trigger`}
      title="Share…"
    >
      <ShareNetwork size={18} weight="bold" />
    </button>
  ) : (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      className={`flex items-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg hover:bg-[#008f86] transition ${className}`}
      data-testid={`${testid}-trigger`}
    >
      <ShareNetwork size={18} weight="bold" />
      {label}
    </button>
  );

  return (
    <div className="relative inline-block" ref={boxRef}>
      {trigger}
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-[#E2F3F0] z-50 overflow-hidden"
          data-testid={`${testid}-panel`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-2 border-b border-[#E2F3F0] text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">
            Share via
          </div>
          <button
            type="button"
            onClick={gmail}
            disabled={loading}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#052A23] hover:bg-[#F4F9F7] transition disabled:opacity-50"
            data-testid={`${testid}-gmail`}
          >
            <GoogleLogo size={18} weight="bold" className="text-[#EA4335]" />
            <div className="text-left">
              <div className="font-semibold">Gmail</div>
              <div className="text-[10px] text-[#6D8E87]">Compose from your Google account</div>
            </div>
          </button>
          <button
            type="button"
            onClick={outlook}
            disabled={loading}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#052A23] hover:bg-[#F4F9F7] transition disabled:opacity-50"
            data-testid={`${testid}-outlook`}
          >
            <MicrosoftOutlookLogo size={18} weight="bold" className="text-[#0078D4]" />
            <div className="text-left">
              <div className="font-semibold">Outlook</div>
              <div className="text-[10px] text-[#6D8E87]">Compose from Outlook / Microsoft 365</div>
            </div>
          </button>
          <button
            type="button"
            onClick={whatsapp}
            disabled={loading}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#052A23] hover:bg-[#F4F9F7] transition disabled:opacity-50 border-t border-[#E2F3F0]"
            data-testid={`${testid}-whatsapp`}
          >
            <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
            <div className="text-left">
              <div className="font-semibold">WhatsApp</div>
              <div className="text-[10px] text-[#6D8E87]">Send via WhatsApp Web / app</div>
            </div>
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              try {
                const p = await getPayload();
                if (!p) return;
                const text = [p.subject, p.body].filter(Boolean).join('\n\n');
                await navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch (e) {
                notify('Could not copy — please select and copy manually.');
              } finally { setLoading(false); }
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-[#6D8E87] hover:bg-[#F4F9F7] transition disabled:opacity-50 border-t border-[#E2F3F0]"
            data-testid={`${testid}-copy`}
          >
            <EnvelopeSimple size={16} />
            {copied ? 'Copied to clipboard ✓' : 'Copy formatted message'}
          </button>
        </div>
      )}
    </div>
  );
}
